import { useMemo, useState, type KeyboardEvent } from 'react';
import {
  KINDS,
  type ConceptKind,
  type ContentItem,
  type ContentPrompt,
  type ContentSequence,
  type ParamDef,
  type ParamValue,
} from '../concepts/kinds';
import { translate, useT } from '../i18n/i18n';

/*
 * THE FORM IS GENERATED, NOT WRITTEN.
 *
 * Every game used to need a hand-built form file — the tile-match one ran to
 * 229 lines of inputs wired up by hand. That is what made adding a kind
 * expensive, and it is why a tile count and a unique-word count ended up on a
 * settings page shared with games that have no tiles. A kind declares what it
 * has; these fields follow.
 *
 * "FOLLOW MY DEFAULT" IS A REAL STATE, AND THE HARD PART. A program stores only
 * what it wants to DIFFER, so every control needs three positions rather than
 * two: unset, or one of the values. A plain number box cannot express "unset",
 * so an empty box means exactly that and the placeholder shows what will be
 * used instead. Without this a supervisor could never change a default and have
 * it move the programs that never disagreed — which is the whole point of
 * having defaults.
 */

type Props = {
  kind: ConceptKind;
  config: Record<string, unknown>;
  /** What the supervisor's settings say for this kind, for showing as the fallback. */
  defaults: Record<string, ParamValue>;
  onChange: (next: Record<string, unknown>) => void;
};

function labelForFallback(param: ParamDef, defaults: Record<string, ParamValue>): string {
  const effective = defaults[param.id] ?? param.fallback;
  if (param.type === 'preset') {
    const match = param.options.find((o) => o.value === effective);
    return match ? translate(match.label) : String(effective);
  }
  if (param.type === 'toggle') return effective ? translate('Yes') : translate('No');
  return String(effective);
}

export default function ParamFields({ kind, config, defaults, onChange }: Props) {
  const t = useT();
  const params = KINDS[kind].params;

  function set(id: string, value: ParamValue | undefined) {
    const next = { ...config };
    /* DELETED, NOT SET TO NULL. An absent key is how "follow my default" is
       stored; a null would be a value the resolver then has to special-case. */
    if (value === undefined) delete next[id];
    else next[id] = value;
    onChange(next);
  }

  return (
    <>
      {params.map((param) => {
        const stated = config[param.id] as ParamValue | undefined;
        const id = `param-${param.id}`;
        const fallbackLabel = labelForFallback(param, defaults);
        return (
          <div className="row" key={param.id}>
            <div className="field">
              <label htmlFor={id}>{t(param.label)}</label>

              {param.type === 'preset' && (
                <select
                  id={id}
                  value={stated === undefined ? '' : String(stated)}
                  onChange={(e) => set(param.id, e.target.value === '' ? undefined : e.target.value)}
                >
                  <option value="">{t('Follow my default — {value}', { value: fallbackLabel })}</option>
                  {param.options.map((o) => (
                    <option key={o.value} value={o.value}>{t(o.label)}</option>
                  ))}
                </select>
              )}

              {param.type === 'toggle' && (
                <select
                  id={id}
                  value={stated === undefined ? '' : stated ? 'yes' : 'no'}
                  onChange={(e) => {
                    const v = e.target.value;
                    set(param.id, v === '' ? undefined : v === 'yes');
                  }}
                >
                  <option value="">{t('Follow my default — {value}', { value: fallbackLabel })}</option>
                  <option value="yes">{t('Yes')}</option>
                  <option value="no">{t('No')}</option>
                </select>
              )}

              {(param.type === 'count' || param.type === 'seconds') && (
                <input
                  id={id}
                  type="number"
                  min={param.min}
                  max={param.max}
                  placeholder={t('Follow my default — {value}', { value: fallbackLabel })}
                  value={stated === undefined ? '' : Number(stated)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { set(param.id, undefined); return; }
                    const n = Number(raw);
                    if (Number.isNaN(n)) return;
                    set(param.id, Math.min(param.max, Math.max(param.min, n)));
                  }}
                />
              )}

              <span className="field-hint">
                {param.hint ? `${t(param.hint)} ` : ''}
                {stated === undefined
                  ? t('Leave as it is to follow your settings.')
                  : t('This program overrides your default.')}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
}

/**
 * What the program teaches: material rather than a setting.
 *
 * TWO SIDES TO EVERY ROW. This was a list of single words, which assumed the
 * prompt and the tile face were the same text. That holds for word matching and
 * fails for most of the concepts — "something red" is answered by an apple, a
 * colour name by a coloured square. A one-box editor could not express either,
 * so half the catalogue was unbuildable and nothing said so.
 *
 * The two boxes accept the same value, which is exactly what plain word
 * matching is, so the simple case costs one extra keystroke rather than a
 * different editor.
 */
export function ContentEditor({
  items, label, hint, sayLabel, answerLabel, maxItems, maxItemLength, onChange,
}: {
  items: ContentItem[];
  label: string;
  hint?: string;
  sayLabel: string;
  answerLabel: string;
  maxItems: number;
  maxItemLength: number;
  onChange: (next: ContentItem[]) => void;
}) {
  const t = useT();
  const [say, setSay] = useState('');
  const [answer, setAnswer] = useState('');
  const [also, setAlso] = useState('');
  const [note, setNote] = useState<string | null>(null);

  /* Faces, not prompts. Two rows may share a prompt; two rows sharing a TILE
     would put two correct answers on the board.

     EVERY NAME COUNTS, not only the main one. A word given as another name for one thing
     cannot also be another thing's answer — the board would still only ever show one of
     them, but the row that lost the race becomes unteachable under that name, which is far
     harder to notice than being told about it here. */
  const faces = useMemo(() => {
    const all = new Set<string>();
    for (const item of items) {
      all.add(item.answer.trim().toLowerCase());
      for (const extra of item.also ?? []) all.add(extra.trim().toLowerCase());
    }
    return all;
  }, [items]);

  function add() {
    const s = say.trim();
    const a = answer.trim();
    if (!s && !a) { setNote(null); return; }
    if (!s || !a) { setNote(t('Both sides are needed — what is said, and what answers it.')); return; }
    if (s.length > maxItemLength || a.length > maxItemLength) {
      setNote(t('Keep each side to {n} characters or fewer.', { n: maxItemLength }));
      return;
    }
    if (faces.has(a.toLowerCase())) {
      setNote(t('"{word}" is already the answer to another row, which would make two tiles correct.', { word: a }));
      return;
    }
    if (items.length >= maxItems) {
      setNote(t('That is the most this can hold ({n}).', { n: maxItems }));
      return;
    }
    const extras: string[] = [];
    for (const raw of also.split(',')) {
      const word = raw.trim();
      if (!word) continue;
      if (word.length > maxItemLength) {
        setNote(t('Keep each word to {n} characters or fewer.', { n: maxItemLength }));
        return;
      }
      const lower = word.toLowerCase();
      if (lower === a.toLowerCase()) continue;
      if (extras.some((x) => x.toLowerCase() === lower)) continue;
      if (faces.has(lower)) {
        setNote(t('"{word}" already belongs to another row, so it cannot also name this one.', { word }));
        return;
      }
      extras.push(word);
    }

    onChange([...items, extras.length > 0 ? { say: s, answer: a, also: extras } : { say: s, answer: a }]);
    setSay('');
    setAnswer('');
    setAlso('');
    setNote(null);
  }

  function key(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  }

  return (
    <div className="field">
      <span className="field-label">{t(label)}</span>
      {hint && <span className="field-hint">{t(hint)}</span>}

      {items.length === 0 ? (
        <p className="muted" style={{ margin: '4px 0' }}>{t('Nothing yet — add a row below.')}</p>
      ) : (
        <ul className="chip-list" aria-label={t(label)}>
          {items.map((item, i) => (
            <li key={`${item.answer}-${i}`} className="chip">
              <span>
                {item.say === item.answer ? item.say : `${item.say} → ${item.answer}`}
                {item.also && item.also.length > 0 ? ` ${t('(also {words})', { words: item.also.join(', ') })}` : ''}
              </span>
              <button
                type="button"
                className="chip-x"
                aria-label={t('Remove {item}', { item: item.say })}
                onClick={() => { onChange(items.filter((_, idx) => idx !== i)); setNote(null); }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="content-say">{t(sayLabel)}</label>
          <input
            id="content-say"
            type="text"
            maxLength={maxItemLength}
            value={say}
            onChange={(e) => { setSay(e.target.value); setNote(null); }}
            onKeyDown={key}
          />
        </div>
        <div className="field">
          <label htmlFor="content-answer">{t(answerLabel)}</label>
          <input
            id="content-answer"
            type="text"
            maxLength={maxItemLength}
            value={answer}
            onChange={(e) => { setAnswer(e.target.value); setNote(null); }}
            onKeyDown={key}
          />
        </div>
        <div className="field">
          <label htmlFor="content-also">{t('Other words for it')}</label>
          <input
            id="content-also"
            type="text"
            value={also}
            placeholder={t('mug, beaker')}
            onChange={(e) => { setAlso(e.target.value); setNote(null); }}
            onKeyDown={key}
          />
          <span className="field-hint">
            {t('Optional, separated by commas. The board shows one of them at a time, never two at once.')}
          </span>
        </div>
      </div>

      <div className="toolbar">
        <button
          type="button"
          className="btn secondary"
          onClick={add}
          disabled={!say.trim() && !answer.trim()}
        >
          {t('Add row')}
        </button>
        {/* The common case, made one press rather than two fields of typing. */}
        <button
          type="button"
          className="btn ghost"
          onClick={() => { setAnswer(say); setNote(null); }}
          disabled={!say.trim() || say.trim() === answer.trim()}
        >
          {t('Same as prompt')}
        </button>
      </div>
      {note && <span className="field-hint">{note}</span>}
    </div>
  );
}

/**
 * What the program teaches, where nothing is tapped.
 *
 * ONE SIDE, NOT TWO. A supervisor-scored trial has no board: the adult gives the
 * prompt and marks what happened. Asking for an "answer" here would store a
 * string nothing ever compares against, and a second box the supervisor has to
 * fill in before the row is accepted.
 */
export function PromptEditor({
  prompts, label, hint, promptLabel, hintLabel, maxItems, maxItemLength, onChange,
}: {
  prompts: ContentPrompt[];
  label: string;
  hint?: string;
  promptLabel: string;
  hintLabel: string;
  maxItems: number;
  maxItemLength: number;
  onChange: (next: ContentPrompt[]) => void;
}) {
  const t = useT();
  const [prompt, setPrompt] = useState('');
  const [note_, setNote_] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  function add() {
    const p = prompt.trim();
    if (!p) { setMessage(null); return; }
    if (p.length > maxItemLength) {
      setMessage(t('Keep each prompt to {n} characters or fewer.', { n: maxItemLength }));
      return;
    }
    if (prompts.some((x) => x.prompt.trim().toLowerCase() === p.toLowerCase())) {
      setMessage(t('"{item}" is already on the list.', { item: p }));
      return;
    }
    if (prompts.length >= maxItems) {
      setMessage(t('That is the most this can hold ({n}).', { n: maxItems }));
      return;
    }
    const n = note_.trim();
    onChange([...prompts, n ? { prompt: p, hint: n } : { prompt: p }]);
    setPrompt('');
    setNote_('');
    setMessage(null);
  }

  function key(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  }

  return (
    <div className="field">
      <span className="field-label">{t(label)}</span>
      {hint && <span className="field-hint">{t(hint)}</span>}

      {prompts.length === 0 ? (
        <p className="muted" style={{ margin: '4px 0' }}>{t('Nothing yet — add a prompt below.')}</p>
      ) : (
        <ul className="chip-list" aria-label={t(label)}>
          {prompts.map((p, i) => (
            <li key={`${p.prompt}-${i}`} className="chip">
              <span>
                {p.prompt}
                {p.hint ? ` (${p.hint})` : ''}
              </span>
              <button
                type="button"
                className="chip-x"
                aria-label={t('Remove {item}', { item: p.prompt })}
                onClick={() => { onChange(prompts.filter((_, idx) => idx !== i)); setMessage(null); }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="prompt-text">{t(promptLabel)}</label>
          <input
            id="prompt-text"
            type="text"
            maxLength={maxItemLength}
            value={prompt}
            onChange={(e) => { setPrompt(e.target.value); setMessage(null); }}
            onKeyDown={key}
          />
        </div>
        <div className="field">
          <label htmlFor="prompt-note">{t(hintLabel)}</label>
          <input
            id="prompt-note"
            type="text"
            maxLength={maxItemLength}
            value={note_}
            onChange={(e) => { setNote_(e.target.value); setMessage(null); }}
            onKeyDown={key}
          />
          <span className="field-hint">
            {t('Optional. Shown to the adult while they score, never to the learner.')}
          </span>
        </div>
      </div>

      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={add} disabled={!prompt.trim()}>
          {t('Add prompt')}
        </button>
      </div>
      {message && <span className="field-hint">{message}</span>}
    </div>
  );
}

/**
 * An order to learn: what is asked for, and the items in the order they go.
 *
 * THE ORDER TYPED IS THE ORDER TAUGHT. The items are entered correctly and shown
 * shuffled, so there is nowhere to state the answer separately and nowhere for
 * the two to disagree.
 */
export function SequenceEditor({
  sequences, label, hint, sequenceLabel, itemsLabel, maxSequences, maxItems, maxItemLength, onChange,
}: {
  sequences: ContentSequence[];
  label: string;
  hint?: string;
  sequenceLabel: string;
  itemsLabel: string;
  maxSequences: number;
  maxItems: number;
  maxItemLength: number;
  onChange: (next: ContentSequence[]) => void;
}) {
  const t = useT();
  const [asked, setAsked] = useState('');
  const [items, setItems] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  function add() {
    const a = asked.trim();
    if (!a) { setMessage(null); return; }
    if (a.length > maxItemLength) {
      setMessage(t('Keep what is asked for to {n} characters or fewer.', { n: maxItemLength }));
      return;
    }
    const parts: string[] = [];
    for (const raw of items.split(',')) {
      const piece = raw.trim();
      if (!piece) continue;
      if (piece.length > maxItemLength) {
        setMessage(t('Keep each item to {n} characters or fewer.', { n: maxItemLength }));
        return;
      }
      if (parts.length >= maxItems) {
        setMessage(t('An order can hold {n} items at most.', { n: maxItems }));
        return;
      }
      parts.push(piece);
    }
    if (parts.length < 2) {
      setMessage(t('An order needs at least two items, separated by commas.'));
      return;
    }
    if (sequences.length >= maxSequences) {
      setMessage(t('That is the most this can hold ({n}).', { n: maxSequences }));
      return;
    }
    onChange([...sequences, { label: a, items: parts }]);
    setAsked('');
    setItems('');
    setMessage(null);
  }

  function key(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  }

  return (
    <div className="field">
      <span className="field-label">{t(label)}</span>
      {hint && <span className="field-hint">{t(hint)}</span>}

      {sequences.length === 0 ? (
        <p className="muted" style={{ margin: '4px 0' }}>{t('Nothing yet — add an order below.')}</p>
      ) : (
        <ul className="chip-list" aria-label={t(label)}>
          {sequences.map((s, i) => (
            <li key={`${s.label}-${i}`} className="chip">
              <span>{s.label} — {s.items.join(' → ')}</span>
              <button
                type="button"
                className="chip-x"
                aria-label={t('Remove {item}', { item: s.label })}
                onClick={() => { onChange(sequences.filter((_, idx) => idx !== i)); setMessage(null); }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="seq-asked">{t(sequenceLabel)}</label>
          <input
            id="seq-asked"
            type="text"
            maxLength={maxItemLength}
            value={asked}
            placeholder={t('Days of the week.')}
            onChange={(e) => { setAsked(e.target.value); setMessage(null); }}
            onKeyDown={key}
          />
        </div>
        <div className="field">
          <label htmlFor="seq-items">{t(itemsLabel)}</label>
          <input
            id="seq-items"
            type="text"
            value={items}
            placeholder={t('Mon, Tue, Wed, Thu, Fri, Sat, Sun')}
            onChange={(e) => { setItems(e.target.value); setMessage(null); }}
            onKeyDown={key}
          />
          <span className="field-hint">
            {t('Separated by commas, in the order they belong in. The learner sees them shuffled.')}
          </span>
        </div>
      </div>

      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={add} disabled={!asked.trim()}>
          {t('Add order')}
        </button>
      </div>
      {message && <span className="field-hint">{message}</span>}
    </div>
  );
}
